import { useEffect, useRef, useState } from 'react';
import type { IslandProps } from '../registry';

type Slide = { src: string; alt: string };

/**
 * Gallery island — the event hero carousel.
 *
 * Mounts into: <div data-react="gallery" data-images='[{"src","alt"}]'></div>
 * The images are server-rendered (16:9 image style) both as the data attribute
 * and as a no-JS fallback; React replaces them with an auto-advancing slider.
 *
 * Behaviour: full-width, one slide at a time, auto-advances left→right every
 * 3s, and responds to touch swipes. Slides are cropped to a uniform 16:9 by the
 * reciklart_carousel image style, so every image fills the frame identically.
 */
export function Gallery({ dataset }: IslandProps) {
  let slides: Slide[] = [];
  try {
    slides = JSON.parse(dataset.images ?? '[]');
  } catch {
    slides = [];
  }
  const count = slides.length;

  const [index, setIndex] = useState(0);
  const touchX = useRef<number | null>(null);
  const go = (i: number) => setIndex(((i % count) + count) % count);

  // Auto-advance every 3s (paused implicitly when a single/no slide).
  useEffect(() => {
    if (count <= 1) return;
    const timer = setInterval(() => setIndex((i) => (i + 1) % count), 3000);
    return () => clearInterval(timer);
  }, [count]);

  if (count === 0) return null;
  if (count === 1) {
    return (
      <div className="reciklart-carousel reciklart-carousel--single">
        <img src={slides[0].src} alt={slides[0].alt} />
      </div>
    );
  }

  return (
    <div
      className="reciklart-carousel"
      role="group"
      aria-roledescription="carousel"
      onTouchStart={(e) => {
        touchX.current = e.touches[0].clientX;
      }}
      onTouchEnd={(e) => {
        if (touchX.current == null) return;
        const dx = e.changedTouches[0].clientX - touchX.current;
        if (Math.abs(dx) > 40) go(index + (dx < 0 ? 1 : -1));
        touchX.current = null;
      }}
    >
      <div
        className="reciklart-carousel__track"
        style={{ transform: `translateX(-${index * 100}%)` }}
      >
        {slides.map((s, i) => (
          <div className="reciklart-carousel__slide" key={i} aria-hidden={i !== index}>
            <img src={s.src} alt={s.alt} />
          </div>
        ))}
      </div>

      <div className="reciklart-carousel__dots">
        {slides.map((_, i) => (
          <button
            key={i}
            type="button"
            className={i === index ? 'is-active' : ''}
            aria-label={`${i + 1}`}
            aria-current={i === index}
            onClick={() => go(i)}
          />
        ))}
      </div>
    </div>
  );
}
